import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useWorkspace from "../../../hooks/useWorkspace";
import AppRoutes from "../../../constants/routes/AppRoutes";
import Workspace, { WorkspaceRole } from "../../../models/workspace/Workspace";
import { getUserRoleLabel } from "../utils/workspaceRoleDisplay";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
} from "@capacit-com/lara-law-shared-ui";
import { DialogDescription } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import {
  Briefcase,
  Building,
  Building2,
  FileText,
  Plus,
  PlusIcon,
  Sparkles,
  Users,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Textarea } from "@capacit-com/lara-law-shared-ui";
import { Separator } from "@capacit-com/lara-law-shared-ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import * as z from "zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@capacit-com/lara-law-shared-ui";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import { Permission } from "@/lib/permissions/Permission.enum";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { logger } from "@/lib/logger";
import Tenant from "@/models/tenant/tenant";

const formSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: "Udfyld navn" })
      .max(50, { message: "Navn må højst være 50 tegn" }),
    description: z
      .string()
      .max(200, { message: "Beskrivelse må højst være 200 tegn" }),
    sharedWithWholeTenant: z.boolean(),
    sharedWorkspaceRole: z.nativeEnum(WorkspaceRole).optional(),
    sharedWithSubTenants: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      // If sharedWithWholeTenant is true, sharedWorkspaceRole must be provided
      if (data.sharedWithWholeTenant && !data.sharedWorkspaceRole) {
        return false;
      }
      return true;
    },
    {
      message:
        "Vælg venligst et adgangsniveau når workspacet deles med organisationen",
      path: ["sharedWorkspaceRole"],
    }
  )
  .refine(
    (data) => {
      // If any subtenants are selected, sharedWithWholeTenant must be true
      if (
        data.sharedWithSubTenants &&
        data.sharedWithSubTenants.length > 0 &&
        !data.sharedWithWholeTenant
      ) {
        return false;
      }
      return true;
    },
    {
      message:
        "Du skal aktivere 'Gør workspacet tilgængeligt for hele organisationen' for at dele med underorganisationer",
      path: ["sharedWithWholeTenant"],
    }
  );

type FormValues = z.infer<typeof formSchema>;

const CreateWorkspaceDialog: FC<{
  availableSubtenants: Tenant[];
  isLoading: boolean;
}> = ({ availableSubtenants, isLoading }) => {
  const { create } = useWorkspace();
  const navigate = useNavigate();
  const hasPermissions = useHasPermission();
  const [isOpen, setIsOpen] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sharedWithWholeTenant: false,
      sharedWorkspaceRole: undefined,
      name: "",
      description: "",
    },
  });

  // The dialog stays mounted between opens, so start every open from a clean form.
  useEffect(() => {
    if (isOpen) form.reset();
  }, [isOpen, form]);

  const handleSubmit = async (values: FormValues) => {
    // Clean up the values to ensure consistent data
    const submitValues = {
      ...values,
      sharedWorkspaceRole: values.sharedWithWholeTenant
        ? values.sharedWorkspaceRole
        : undefined,
    };

    await create.mutateAsync(submitValues).then((resp) => {
      const parsedResponse = resp as Workspace;
      if (parsedResponse.id) {
        navigate(AppRoutes.workspaceFiles(parsedResponse.id));
      } else
        logger.error("Created workspace response has no workspace id", {
          operation: "createWorkspace",
        });
    });
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              className="ml-2"
              disabled={isLoading}
              onClick={() => setIsOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Opret
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Opret nyt workspace</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <FormDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        isDirty={form.formState.isDirty}
        size="md"
      >
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 p-2 rounded-full">
              <Briefcase className="h-5 w-5 text-primary" />
            </div>
            <DialogTitle className="text-xl">Opret Workspace</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">
            Opret et nyt workspace til at organisere dine projekter og
            samarbejde med dit team.
          </DialogDescription>
        </DialogHeader>

        <Separator className="mt-2" />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="space-y-6 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-16rem)] py-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      Workspace navn
                    </FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="F.eks. Marketing Team"
                          className="pl-9"
                          data-testid="name"
                        />
                      </FormControl>
                      <Briefcase className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-primary" />
                      Beskrivelse
                    </FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Beskriv formålet med dette workspace..."
                          className="min-h-[100px] pl-9"
                          data-testid="description"
                        />
                      </FormControl>
                      <FileText className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    </div>
                    <FormMessage />
                    <FormDescription>
                      En god beskrivelse hjælper teammedlemmer med at forstå
                      formålet med workspacet.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sharedWithWholeTenant"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 justify-between">
                    <FormLabel className="flex items-center gap-1.5 cursor-pointer">
                      <Building className="h-3.5 w-3.5 text-primary" />
                      Gør workspacet tilgængeligt for hele organisationen
                    </FormLabel>
                    <div className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          className="cursor-pointer"
                          disabled={
                            !hasPermissions({
                              allOf: [Permission.CreateWorkspaceOrgShared],
                            })
                          }
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            if (!checked) {
                              form.setValue("sharedWorkspaceRole", undefined, {
                                shouldValidate: true,
                              });
                            } else {
                              form.setValue(
                                "sharedWorkspaceRole",
                                WorkspaceRole.READ,
                                { shouldValidate: true }
                              );
                            }
                          }}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("sharedWithWholeTenant") && (
                <FormField
                  control={form.control}
                  name="sharedWorkspaceRole"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-primary" />
                        Vælg adgangsniveau for organisationen
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Vælg adgangsniveau" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={WorkspaceRole.READ}>
                            {getUserRoleLabel(WorkspaceRole.READ)}
                          </SelectItem>
                          <SelectItem value={WorkspaceRole.WRITE}>
                            {getUserRoleLabel(WorkspaceRole.WRITE)}
                          </SelectItem>
                          <SelectItem value={WorkspaceRole.ADMIN}>
                            {getUserRoleLabel(WorkspaceRole.ADMIN)}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="sharedWithSubTenants"
                render={({ field }) => {
                  if (availableSubtenants.length === 0) return <></>;

                  const hasSelectedSubtenants =
                    field.value && field.value.length > 0;
                  const isOrgSharingEnabled = form.watch(
                    "sharedWithWholeTenant"
                  );
                  const showWarning =
                    hasSelectedSubtenants && !isOrgSharingEnabled;

                  return (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-primary" />
                          Del med underorganisationer
                        </FormLabel>
                        <div className="flex items-center space-x-2">
                          <label
                            htmlFor="select-all-subtenants"
                            className="text-sm font-medium leading-none cursor-pointer"
                          >
                            Vælg alle
                          </label>
                          <Checkbox
                            id="select-all-subtenants"
                            checked={
                              field.value?.length ===
                                availableSubtenants.length &&
                              availableSubtenants.length > 0
                            }
                            className="hover:cursor-pointer"
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange(
                                  availableSubtenants.map(
                                    (subtenant) => subtenant.id
                                  )
                                );
                              } else {
                                field.onChange([]);
                              }
                            }}
                          />
                        </div>
                      </div>
                      {showWarning && (
                        <div className="text-xs text-warning bg-warning/10 p-2 rounded-md border border-warning/30 mt-2">
                          <strong>Bemærk:</strong> For at dele med
                          underorganisationer skal du først aktivere "Gør
                          workspacet tilgængeligt for hele organisationen"
                          ovenfor.
                        </div>
                      )}
                      <div className="border rounded-md p-3 max-h-48 overflow-y-auto">
                        <div className="space-y-2 grid grid-cols-2 gap-2">
                          {availableSubtenants.map((subtenant) => (
                            <div
                              key={subtenant.id}
                              className="flex items-center space-x-2 mb-0"
                            >
                              <Checkbox
                                id={`subtenant-${subtenant.id}`}
                                checked={
                                  field.value?.includes(subtenant.id) || false
                                }
                                onCheckedChange={(checked) => {
                                  const currentValues = field.value || [];
                                  if (checked) {
                                    field.onChange([
                                      ...currentValues,
                                      subtenant.id,
                                    ]);
                                  } else {
                                    field.onChange(
                                      currentValues.filter(
                                        (id) => id !== subtenant.id
                                      )
                                    );
                                  }
                                }}
                              />
                              <label
                                htmlFor={`subtenant-${subtenant.id}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                              >
                                {subtenant.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs">
                        Obs. Underorganisationer får kun læse rettigheder
                      </p>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
            <DialogFooter className="gap-2 space-x-2 sm:gap-0 mt-6">
              <FormDialogCancel />
              <div className="flex flex-col items-end gap-2">
                {!form.formState.isValid &&
                  form.formState.errors.sharedWithWholeTenant && (
                    <div className="text-xs text-warning bg-warning/10 p-2 rounded-md border border-warning/30 max-w-xs">
                      <strong>Krav:</strong> Aktivér organisationsdeling for at
                      dele med underorganisationer
                    </div>
                  )}
                <Button
                  type="submit"
                  disabled={
                    create.isPending ||
                    !form.formState.isDirty ||
                    !form.formState.isValid
                  }
                  className="gap-2 min-w-[100px]"
                >
                  {create.isPending ? (
                    <>
                      <Spinner
                        size={"small"}
                        className="text-primary-foreground"
                      />
                      <span>Opretter...</span>
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-4 w-4" />
                      <span>Opret workspace</span>
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </FormDialog>
    </>
  );
};

export default CreateWorkspaceDialog;
